<template>
  <div class="media-grid-container">
    <!-- Loading state -->
    <div v-if="loading && !media.length" class="text-center py-3">
      <CSpinner color="primary" size="sm"/>
      <span class="ml-2 text-muted">
        <slot name="loading-text">
          <translate translate-context="media.grid">Loading photos...</translate>
        </slot>
      </span>
    </div>

    <!-- Empty state -->
    <div v-else-if="!media.length" class="text-center py-3 text-muted">
      <CIcon name="cipImage" size="lg" class="mr-2"/>
      <slot name="empty-text">
        <translate translate-context="media.grid">No photos available</translate>
      </slot>
    </div>

    <!-- Media grid -->
    <div v-else>
      <div
        class="media-grid-wrapper"
        :class="{
          'expanded': isExpanded,
          'auto-expanded': shouldAutoExpand,
          [`mode-${mode}`]: true
        }"
      >
        <!-- Expand button (floating) -->
        <div v-if="expandable && canExpand && !isExpanded" class="media-grid-expand-floating">
          <CButton
            color="primary"
            size="sm"
            @click="toggleExpand"
            class="shadow"
          >
            <CIcon name="cipArrowNarrowDown" size="sm" class="mr-1"/>
            <translate translate-context="media.grid"
                      :translate-params="{count: totalCount}">
              Show all %{count} photos
            </translate>
          </CButton>
        </div>

        <!-- Grid -->
        <div
          class="media-grid"
          :style="gridStyle"
        >
          <MediaItem
            v-for="mediaItem in displayedMedia"
            :key="mediaItem.id"
            :media="mediaItem"
            :selected="isSelected(mediaItem.id)"
            :hovering="isHovering(mediaItem.id)"
            :badge="getBadge(mediaItem.id)"
            :selectable="selectable"
            :zoomable="zoomable"
            @click="handleItemClick"
            @mouseenter="handleItemMouseEnter"
            @mouseleave="handleItemMouseLeave"
            @zoom="handleZoom"
          />
        </div>

        <!-- Fade overlay when collapsed -->
        <div v-if="!isExpanded && !shouldAutoExpand && canExpand" class="media-grid-fade">
          <div class="fade-gradient"></div>
        </div>

        <!-- Collapse button (floating) -->
        <div v-if="expandable && canExpand && isExpanded" class="media-grid-collapse-floating">
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            @click="toggleExpand"
            class="shadow-sm"
          >
            <CIcon name="cipArrowNarrowUp" size="sm" class="mr-1"/>
            <translate translate-context="media.grid">Show less</translate>
          </CButton>
        </div>
      </div>

      <!-- Load more button -->
      <div v-if="hasMore && !expandable" class="text-center mt-3">
        <CButton
          color="primary"
          variant="outline"
          @click="$emit('load-more')"
          :disabled="loading"
        >
          <CSpinner v-if="loading" size="sm" class="mr-2"/>
          <translate translate-context="media.grid">Load More</translate>
        </CButton>
      </div>
    </div>
  </div>
</template>

<script>
import MediaItem from './MediaItem.vue'

export default {
  name: 'MediaGrid',
  components: {
    MediaItem
  },
  props: {
    // Data
    media: {
      type: Array,
      required: true,
      // Expected: [{ id, thumb, sd?, hd? }]
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    badges: {
      type: Object,
      default: () => ({})
      // Format: { [mediaId]: { text, color, icon } }
    },

    // States
    loading: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: null
    },

    // Behavior
    selectable: {
      type: Boolean,
      default: true
    },
    zoomable: {
      type: Boolean,
      default: true
    },
    expandable: {
      type: Boolean,
      default: false
    },

    // Display mode
    mode: {
      type: String,
      default: 'preview',
      validator: val => ['preview', 'gallery'].includes(val)
    },

    // Grid configuration
    itemsPerRow: {
      type: Number,
      default: null // Auto-calculate if null
    },
    previewRows: {
      type: Number,
      default: 2
    },
    itemMinWidth: {
      type: Number,
      default: 150 // px
    },
    gap: {
      type: Number,
      default: 10 // px
    }
  },

  data() {
    return {
      isExpanded: false,
      calculatedItemsPerRow: this.itemsPerRow || 5,
      hoveringIds: {} // For shift+hover preview
    }
  },

  computed: {
    shouldAutoExpand() {
      if (!this.expandable) return false
      const total = this.totalCount || this.media.length
      const maxInPreview = this.calculatedItemsPerRow * this.previewRows
      return total <= maxInPreview
    },

    displayedMedia() {
      if (this.isExpanded || this.shouldAutoExpand || !this.expandable) {
        return this.media
      }
      // Show previewRows worth of items
      const itemsToShow = this.calculatedItemsPerRow * this.previewRows
      return this.media.slice(0, itemsToShow)
    },

    canExpand() {
      if (!this.expandable || this.shouldAutoExpand) return false
      const total = this.totalCount || this.media.length
      const previewLimit = this.calculatedItemsPerRow * this.previewRows
      return total > previewLimit
    },

    gridStyle() {
      return {
        gridTemplateColumns: `repeat(auto-fill, minmax(${this.itemMinWidth}px, 1fr))`,
        gap: `${this.gap}px`
      }
    }
  },

  methods: {
    isSelected(mediaId) {
      return this.selectedIds.includes(mediaId)
    },

    isHovering(mediaId) {
      return this.hoveringIds[mediaId] === true
    },

    getBadge(mediaId) {
      return this.badges[mediaId] || null
    },

    handleItemClick(event, mediaId) {
      if (!this.selectable) return
      this.$emit('select', { event, mediaId })
    },

    handleItemMouseEnter(mediaId) {
      this.$emit('hover', mediaId)
    },

    handleItemMouseLeave() {
      this.$emit('hover-end')
    },

    handleZoom(media) {
      if (!this.zoomable) return
      this.$emit('zoom', media)
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded
      this.$emit('expand', this.isExpanded)
    },

    setHoveringIds(ids) {
      // Called from parent for shift+click hover preview
      this.hoveringIds = ids
    },

    calculateItemsPerRow() {
      if (this.itemsPerRow) {
        this.calculatedItemsPerRow = this.itemsPerRow
        return
      }

      if (!this.$el) return

      const grid = this.$el.querySelector('.media-grid')
      if (!grid) return

      const containerWidth = grid.offsetWidth
      this.calculatedItemsPerRow = Math.floor((containerWidth + this.gap) / (this.itemMinWidth + this.gap))
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.calculateItemsPerRow()
    })
    window.addEventListener('resize', this.calculateItemsPerRow)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calculateItemsPerRow)
  }
}
</script>

<style scoped>
.media-grid-container {
  position: relative;
}

.media-grid-wrapper {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

/* Preview mode - collapsed state (shows ~1.5 rows) */
.media-grid-wrapper.mode-preview:not(.expanded):not(.auto-expanded) {
  max-height: 280px;
}

/* Gallery mode - always expanded */
.media-grid-wrapper.mode-gallery {
  max-height: none;
}

.media-grid-wrapper.expanded,
.media-grid-wrapper.auto-expanded {
  max-height: none;
}

.media-grid {
  display: grid;
  padding-bottom: 20px;
}

.media-grid-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  pointer-events: none;
  z-index: 5;
}

.fade-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 0.95) 100%
  );
}

.media-grid-expand-floating {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
}

.media-grid-collapse-floating {
  position: sticky;
  bottom: 20px;
  display: flex;
  justify-content: center;
  margin-top: -40px;
  z-index: 10;
  pointer-events: none;
}

.media-grid-collapse-floating .btn {
  pointer-events: auto;
}

/* Responsive adjustments */
@media (min-width: 1200px) {
  .media-grid-wrapper.mode-preview:not(.expanded):not(.auto-expanded) {
    max-height: 290px;
  }
}

@media (max-width: 768px) {
  .media-grid-wrapper.mode-preview:not(.expanded):not(.auto-expanded) {
    max-height: 220px;
  }
}

@media (max-width: 576px) {
  .media-grid-wrapper.mode-preview:not(.expanded):not(.auto-expanded) {
    max-height: 190px;
  }
}
</style>
